'use client';

import React, { createContext, useContext } from 'react';
import { Heading } from 'react-aria-components';
import { cx } from '../../../utils/cx';

const SEMANTIC_NAME_MODAL_HEADER = 'RomaModalHeader';
const SEMANTIC_NAME_MODAL_HEADER_TITLE = 'RomaModalHeaderTitle';
const SEMANTIC_NAME_MODAL_HEADER_SUPPORTING_TEXT = 'RomaModalHeaderSupportingText';

type ModalHeaderSize = 'sm' | 'md' | 'lg';
type ModalHeaderVariant = 'default' | 'centered';

type ModalHeaderContextValue = {
    size: ModalHeaderSize;
    variant: ModalHeaderVariant;
};

export type ModalHeaderProps = React.HTMLAttributes<HTMLDivElement> & {
    size?: ModalHeaderSize;
    variant?: ModalHeaderVariant;
};

export type ModalHeaderTitleProps = Omit<React.ComponentPropsWithoutRef<typeof Heading>, 'level' | 'slot'>;
export type ModalHeaderSupportingTextProps = React.HTMLAttributes<HTMLParagraphElement>;

const ModalHeaderContext = createContext<ModalHeaderContextValue>({ size: 'sm', variant: 'default' });

const headerStyles = {
    sm: {
        content: 'gap-xxs',
        title: 'text-lg',
    },
    md: {
        content: 'gap-xxs px-3xl',
        title: 'text-display-md',
    },
    lg: {
        content: 'gap-xl',
        title: 'text-display-xl',
    },
};

/** Provides the standard structure and spacing for a modal header. */
const ModalHeaderRoot: React.FC<ModalHeaderProps> = ({
    size = 'sm',
    variant = 'default',
    className,
    children,
    ...props
}) => (
    <ModalHeaderContext.Provider value={{ size, variant }}>
        <div
            {...props}
            className={cx(
                SEMANTIC_NAME_MODAL_HEADER,
                'relative flex w-full shrink-0 flex-col items-start break-words px-4xl pt-3xl pb-2xl',
                headerStyles[size].content,
                variant === 'centered' && 'items-center text-center',
                className,
            )}
        >
            {children}
        </div>
    </ModalHeaderContext.Provider>
);

ModalHeaderRoot.displayName = SEMANTIC_NAME_MODAL_HEADER;

/** Labels the containing React Aria dialog. */
const ModalHeaderTitle: React.FC<ModalHeaderTitleProps> = ({ className, ...props }) => {
    const { size, variant } = useContext(ModalHeaderContext);

    return (
        <Heading
            {...props}
            level={2}
            slot="title"
            className={cx(
                SEMANTIC_NAME_MODAL_HEADER_TITLE,
                'm-0 w-full font-semibold text-primary',
                variant === 'default' && 'in-data-close-button:pr-14',
                variant === 'centered' && 'in-data-close-button:px-14',
                headerStyles[size].title,
                className,
            )}
        />
    );
};

ModalHeaderTitle.displayName = SEMANTIC_NAME_MODAL_HEADER_TITLE;

/** Renders supporting copy beneath a modal title. */
const ModalHeaderSupportingText: React.FC<ModalHeaderSupportingTextProps> = ({ className, ...props }) => (
    <p
        {...props}
        className={cx(SEMANTIC_NAME_MODAL_HEADER_SUPPORTING_TEXT, 'm-0 w-full text-md text-tertiary', className)}
    />
);

ModalHeaderSupportingText.displayName = SEMANTIC_NAME_MODAL_HEADER_SUPPORTING_TEXT;

type ModalHeaderComponent = typeof ModalHeaderRoot & {
    Title: typeof ModalHeaderTitle;
    SupportingText: typeof ModalHeaderSupportingText;
};

export const ModalHeader = Object.assign(ModalHeaderRoot, {
    Title: ModalHeaderTitle,
    SupportingText: ModalHeaderSupportingText,
}) as ModalHeaderComponent;
